import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import type {
  CreateHostedAgentArtifactRequest,
  HostedAgentArtifact,
  HostedAgentArtifactWithFamily,
  UpdateHostedAgentArtifactRequest,
} from "@/api/client/hosted-agents-artifacts"

import {
  createHostedAgentArtifactApi,
  deleteHostedAgentArtifactApi,
  getHostedAgentArtifactApi,
  listHostedAgentArtifactsApi,
  updateHostedAgentArtifactApi,
} from "@/api/client/hosted-agents-artifacts"
import { queryKeys } from "@/lib/query-keys"

/** Artifact library for one agent: latest version per family, newest first. */
export function useHostedAgentArtifacts(slug: string | undefined) {
  return useQuery<HostedAgentArtifact[]>({
    queryKey: queryKeys.hostedAgentArtifacts(slug ?? ""),
    queryFn: () => listHostedAgentArtifactsApi(slug ?? ""),
    enabled: !!slug,
  })
}

/** One artifact plus its version family. */
export function useHostedAgentArtifact(slug: string | undefined, artifactId: string | undefined) {
  return useQuery<HostedAgentArtifactWithFamily>({
    queryKey: queryKeys.hostedAgentArtifact(slug ?? "", artifactId ?? ""),
    queryFn: () => getHostedAgentArtifactApi(slug ?? "", artifactId ?? ""),
    enabled: !!slug && !!artifactId,
    // Re-anchoring on a sibling version (after a delete) keeps the family
    // list on screen while the fresh one loads.
    placeholderData: (previous) => previous,
  })
}

function useInvalidateArtifacts(slug: string | undefined) {
  const queryClient = useQueryClient()
  return () => {
    if (!slug) return
    void queryClient.invalidateQueries({ queryKey: queryKeys.hostedAgentArtifacts(slug) })
  }
}

export function useCreateHostedAgentArtifact(slug: string | undefined) {
  const invalidate = useInvalidateArtifacts(slug)
  return useMutation({
    mutationFn: (body: CreateHostedAgentArtifactRequest) =>
      createHostedAgentArtifactApi(slug ?? "", body),
    onSuccess: invalidate,
  })
}

export function useUpdateHostedAgentArtifact(slug: string | undefined) {
  const invalidate = useInvalidateArtifacts(slug)
  return useMutation({
    mutationFn: ({
      artifactId,
      body,
    }: {
      artifactId: string
      body: UpdateHostedAgentArtifactRequest
    }) => updateHostedAgentArtifactApi(slug ?? "", artifactId, body),
    onSuccess: invalidate,
  })
}

export function useDeleteHostedAgentArtifact(slug: string | undefined) {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateArtifacts(slug)
  return useMutation({
    mutationFn: (artifactId: string) => deleteHostedAgentArtifactApi(slug ?? "", artifactId),
    onSuccess: (_data, artifactId) => {
      // Drop the deleted version's own query first: invalidating it would
      // re-GET the deleted id and 404 (#3207). Sibling families still refresh.
      if (slug) {
        queryClient.removeQueries({
          queryKey: queryKeys.hostedAgentArtifact(slug, artifactId),
          exact: true,
        })
      }
      invalidate()
    },
  })
}
